import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Check, Loader2, ExternalLink, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import { referenceDataApi } from '@/api/referenceDataApi';
import type { RefSupplier, RefColor, RefBrand } from '@/types/api';

type RefType = 'suppliers' | 'colors' | 'brands';

interface ReferenceDataManagerProps {
  type: RefType;
}

export function ReferenceDataManager({ type }: ReferenceDataManagerProps) {
  const [items, setItems] = useState<(RefSupplier | RefColor | RefBrand)[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const { success: showSuccess, error: showError } = useToast();

  // Form state
  const [formName, setFormName] = useState('');
  const [formWebsiteUrl, setFormWebsiteUrl] = useState('');
  const [formHexCode, setFormHexCode] = useState('#000000');

  const fetchData = async () => {
    setLoading(true);
    try {
      let data;
      switch (type) {
        case 'suppliers':
          data = await referenceDataApi.getSuppliers();
          break;
        case 'colors':
          data = await referenceDataApi.getColors();
          break;
        case 'brands':
          data = await referenceDataApi.getBrands();
          break;
      }
      setItems(data);
    } catch (err) {
      showError(`Failed to load ${type}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [type]);

  const resetForm = () => {
    setFormName('');
    setFormWebsiteUrl('');
    setFormHexCode('#000000');
    setEditingId(null);
    setShowAdd(false);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      showError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        switch (type) {
          case 'suppliers':
            await referenceDataApi.updateSupplier(editingId, {
              name: formName.trim(),
              websiteUrl: formWebsiteUrl.trim() || undefined,
            });
            break;
          case 'colors':
            await referenceDataApi.updateColor(editingId, {
              name: formName.trim(),
              hexCode: formHexCode,
            });
            break;
          case 'brands':
            await referenceDataApi.updateBrand(editingId, {
              name: formName.trim(),
            });
            break;
        }
        showSuccess('Updated successfully');
      } else {
        // Create new
        switch (type) {
          case 'suppliers':
            await referenceDataApi.createSupplier({
              name: formName.trim(),
              websiteUrl: formWebsiteUrl.trim() || undefined,
            });
            break;
          case 'colors':
            await referenceDataApi.createColor({
              name: formName.trim(),
              hexCode: formHexCode,
            });
            break;
          case 'brands':
            await referenceDataApi.createBrand({
              name: formName.trim(),
            });
            break;
        }
        showSuccess('Created successfully');
      }
      resetForm();
      fetchData();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to save';
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: RefSupplier | RefColor | RefBrand) => {
    setFormName(item.name);
    if (type === 'suppliers') {
      setFormWebsiteUrl((item as RefSupplier).websiteUrl || '');
    }
    if (type === 'colors') {
      setFormHexCode((item as RefColor).hexCode);
    }
    setEditingId(item.id);
    setShowAdd(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      switch (type) {
        case 'suppliers':
          await referenceDataApi.deleteSupplier(id);
          break;
        case 'colors':
          await referenceDataApi.deleteColor(id);
          break;
        case 'brands':
          await referenceDataApi.deleteBrand(id);
          break;
      }
      showSuccess('Deleted successfully');
      fetchData();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to delete';
      showError(message);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'suppliers': return 'Suppliers';
      case 'colors': return 'Colors';
      case 'brands': return 'Brands';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'suppliers': return 'Manage suppliers for your supplies and filaments';
      case 'colors': return 'Manage colors for filaments and materials';
      case 'brands': return 'Manage brands for your products';
    }
  };

  const getPlaceholder = () => {
    switch (type) {
      case 'suppliers': return 'e.g., Hatchbox';
      case 'colors': return 'e.g., Galaxy Black';
      case 'brands': return 'e.g., Bambu Lab';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-450" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">{getTitle()}</h3>
          <p className="text-sm text-gray-600 dark:text-warm-sand">{getDescription()}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAdd(true)}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add {type.slice(0, -1)}
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                  Name *
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={getPlaceholder()}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:border-amber-450 transition-all"
                  autoFocus
                />
              </div>
              
              {type === 'suppliers' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={formWebsiteUrl}
                    onChange={(e) => setFormWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:border-amber-450 transition-all"
                  />
                </div>
              )}
              
              {type === 'colors' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                    Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formHexCode}
                      onChange={(e) => setFormHexCode(e.target.value)}
                      className="h-10 w-14 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formHexCode}
                      onChange={(e) => setFormHexCode(e.target.value)}
                      placeholder="#000000"
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:border-amber-450 transition-all uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 pt-7">
              <Button
                variant="ghost"
                size="sm"
                onClick={resetForm}
                disabled={saving}
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={saving}
              >
                <Check className="w-4 h-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-warm-sand">No {type} yet.</p>
            <p className="text-sm text-warm-taupe mt-1">
              Click "Add {type.slice(0, -1)}" to create your first one.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-charcoal-700 max-h-96 overflow-y-auto">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Color swatch for colors */}
                  {type === 'colors' && (
                    <div
                      className="w-8 h-8 rounded-lg border border-gray-300 dark:border-charcoal-600 shadow-sm"
                      style={{ backgroundColor: (item as RefColor).hexCode }}
                    />
                  )}
                  
                  <div>
                    <p className="font-medium text-gray-900 dark:text-warm-cream">{item.name}</p>
                    {type === 'suppliers' && (item as RefSupplier).websiteUrl && (
                      <a
                        href={(item as RefSupplier).websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-450 hover:text-amber-600 dark:text-amber-400 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(item as RefSupplier).websiteUrl?.replace(/^https?:\/\//, '').substring(0, 30)}
                        {(item as RefSupplier).websiteUrl && (item as RefSupplier).websiteUrl!.length > 30 ? '...' : ''}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {type === 'colors' && (
                      <p className="text-xs text-warm-taupe font-mono">
                        {(item as RefColor).hexCode}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.name)}
                    className="p-2 text-gray-600 dark:text-warm-sand hover:text-danger-400 hover:bg-danger-500/10 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Info note */}
      <div className="flex items-start gap-2 text-xs text-warm-taupe">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          These {type} are used throughout the system for dropdown selections. 
          Changes will be reflected immediately in all forms.
        </p>
      </div>
    </div>
  );
}
